<template>
  <div class="p-4 overflow-y-auto h-full fade-in">
    <a
      @click="handleBack"
      class="text-xs text-gray-500 hover:text-gray-700 flex items-center mb-2 rounded-lg hover:bg-gray-200 w-fit p-2 px-4 pl-3 transition-all duration-200 cursor-pointer"
    >
      <ArrowLeft class="w-4 h-4 mr-2" />
      Back
    </a>
    <form
      @submit.prevent="onSubmit"
      class="space-y-4 max-w-4xl mx-auto bg-white rounded-lg p-8 border-[1px] border-gray-200"
    >
      <span class="flex items-center gap-2">
        <SquareUserRound class="w-4 h-4" />
        <h2>Basic Information</h2>
        <Loader2 v-if="userStore.isLoading" class="w-4 h-4 animate-spin" />
      </span>

      <div class="max-w-2xl mx-auto space-y-4">
        <!-- <div class="flex items-center gap-4 relative">
          <div class="relative">
            <div
              v-if="userStore.avatarLoading"
              class="absolute z-20 inset-0 flex items-center justify-center bg-gray-100/70 rounded-full backdrop-blur-[1px]"
            >
              <Loader2 class="w-6 h-6 animate-spin text-black" />
            </div>
            <img
              v-if="userStore.currentUser?.avatar_image"
              :src="userStore.currentUser.avatar_image"
              alt="Avatar"
              class="w-28 h-28 rounded-full object-cover border"
            />
            <div
              v-else
              class="w-28 h-28 rounded-full bg-gray-100 flex items-center justify-center border"
            >
              <UserRound class="w-10 h-10 text-gray-400" />
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <Input
              id="avatar-upload"
              type="file"
              class="hidden"
              @change="handleFileSelection"
              accept="image/png, image/jpeg, image/gif"
              :key="fileInputKey"
            />
            <Button
              as="label"
              for="avatar-upload"
              variant="outline"
              size="sm"
              :disabled="userStore.avatarLoading"
            >
              Change Picture
            </Button>
            <Button
              v-if="userStore.currentUser?.avatar_image"
              size="sm"
              @click="removeAvatar"
              :disabled="userStore.isLoading || userStore.avatarLoading"
            >
              Remove Picture
            </Button>
          </div>
        </div> -->
        <ProfilePictureManager
          :is-loading="userStore.avatarLoading"
          :profile-image="userStore.currentUser?.avatar_image"
          :is-own-profile="true"
          @update:profile-image="uploadAvatar"
          @remove-avatar="removeAvatar"
        />
        
        <FormField v-slot="{ componentField }" name="username" class="">
          <FormItem class="space-y-1">
            <FormLabel>Username</FormLabel>
            <FormControl>
              <Input type="text" placeholder="Your username" v-bind="componentField" @input="handleUsernameInput" @blur="handleUsernameBlur" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField }" name="email" class="sm:col-span-3">
          <FormItem class="space-y-1">
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="your.email@example.com" v-bind="componentField" @input="handleEmailInput" @blur="handleEmailBlur" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField }" name="first_name" class="sm:col-span-3">
          <FormItem class="space-y-1">
            <FormLabel>First name</FormLabel>
            <FormControl>
              <Input type="text" placeholder="Your first name" v-bind="componentField" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField }" name="last_name" class="sm:col-span-3">
          <FormItem class="space-y-1">
            <FormLabel>Last name</FormLabel>
            <FormControl>
              <Input type="text" placeholder="Your last name" v-bind="componentField" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
      </div>
      <div class="flex justify-end pt-4">
        <Button
          type="submit"
          :disabled="userStore.isLoading || !isDirty || !isValid"
          class="bg-cosmicPurple text-white hover:bg-cosmicPurple/80 cursor-pointer text-xs"
        >
          Save Changes
        </Button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from 'vue'
import { SquareUserRound, ArrowLeft, Loader2 } from 'lucide-vue-next'
import { useUserStore } from '@/stores/user'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import { toast } from '@/components/ui/toast/use-toast'
import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import ProfilePictureManager from '@/components/common/ProfilePictureManager.vue'

import type { UserUpdatePayload, User } from '@/types/user'
import { useRouter, useRoute } from 'vue-router'

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const fileInputKey = ref(0)

const originalUsername = ref('')
const originalEmail = ref('')
const usernameTimer = ref<number | null>(null)
const emailTimer = ref<number | null>(null)
const isCheckingUsername = ref(false)
const isCheckingEmail = ref(false)

// Create a custom validation state
const validationState = ref({
  usernameValid: true,
  emailValid: true
})

const formSchema = toTypedSchema(
  z.object({
    username: z.string()
      .min(2, { message: 'Username must be at least 2 characters.' })
      .max(50),
    email: z.string()
      .email({ message: 'Please enter a valid email address.' }),
    first_name: z.string().min(1, { message: 'First name is required.' }).max(50),
    last_name: z.string().min(1, { message: 'Last name is required.' }).max(50),
  })
)

const { handleSubmit, resetForm, meta, values, setFieldError, setErrors } = useForm({
  validationSchema: formSchema
})

const validateUsername = () => {
  if (usernameTimer.value) {
    clearTimeout(usernameTimer.value)
    usernameTimer.value = null
  }
  
  if (values.username === originalUsername.value || isCheckingUsername.value) {
    return
  }
  
  usernameTimer.value = setTimeout(async () => {
    isCheckingUsername.value = true
    try {
      const result = await userStore.checkUsernameUniqueness(values.username || '')
      validationState.value.usernameValid = result.valid !== false
      if (!validationState.value.usernameValid) {
        setFieldError('username', 'This username is already taken')
      }
    } catch (error) {
      console.error('Error checking username uniqueness:', error)
    } finally {
      isCheckingUsername.value = false
    }
  }, 1000)
}

const validateEmail = () => {
  if (emailTimer.value) {
    clearTimeout(emailTimer.value)
    emailTimer.value = null
  }
  
  if (values.email === originalEmail.value || isCheckingEmail.value) {
    return
  }
  
  emailTimer.value = setTimeout(async () => {
    isCheckingEmail.value = true
    try {
      const result = await userStore.checkEmailUniqueness(values.email || '')
      validationState.value.emailValid = result.valid !== false
      if (!validationState.value.emailValid) {
        setFieldError('email', 'This email is already registered')
      }
    } catch (error) {
      console.error('Error checking email uniqueness:', error)
    } finally {
      isCheckingEmail.value = false
    }
  }, 1000)
}

// Add event handlers that manually track focus
const handleUsernameInput = () => {
  validateUsername()
}

const handleUsernameBlur = () => {
  if (!validationState.value.usernameValid) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    nextTick(() => {
      setFieldError('username', 'This username is already taken')
    })
  }
}

const handleEmailInput = () => {
  validateEmail()
}

const handleEmailBlur = () => {
  if (!validationState.value.emailValid) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    nextTick(() => {
      setFieldError('email', 'This email is already registered')
    })
  }
}

// Update the isValid computed property
const isDirty = computed(() => meta.value.dirty)
const isValid = computed(() => 
  meta.value.valid && 
  validationState.value.usernameValid && 
  validationState.value.emailValid
)

onMounted(async () => {
  if (!userStore.currentUser) {
    await userStore.fetchCurrentUser()
  }

  const currentUser = userStore.currentUser as User | null
  if (currentUser) {
    originalUsername.value = currentUser.username
    originalEmail.value = currentUser.email

    const initialValues = {
      username: currentUser.username,
      email: currentUser.email,
      first_name: currentUser.first_name,
      last_name: currentUser.last_name,
    }
    resetForm({ values: initialValues })
  }
})

// eslint-disable-next-line complexity
const onSubmit = handleSubmit(async (values) => {
  if (usernameTimer.value) clearTimeout(usernameTimer.value)
  if (emailTimer.value) clearTimeout(emailTimer.value)
  
  // Check if we have any custom validation errors
  if (!validationState.value.usernameValid || !validationState.value.emailValid) {
    return
  }
  
  if (values.username !== originalUsername.value) {
    try {
      const usernameResult = await userStore.checkUsernameUniqueness(values.username || '')
      if (usernameResult.valid === false) {
        validationState.value.usernameValid = false
        setFieldError('username', 'This username is already taken')
        return
      }
    } catch (error) {
      console.error('Error in final username check:', error)
    }
  }
  
  if (values.email !== originalEmail.value) {
    try {
      const emailResult = await userStore.checkEmailUniqueness(values.email || '')
      if (emailResult.valid === false) {
        validationState.value.emailValid = false
        setFieldError('email', 'This email is already registered')
        return
      }
    } catch (error) {
      console.error('Error in final email check:', error)
    }
  }

  const payload: UserUpdatePayload = {
    username: values.username,
    email: values.email,
    first_name: values.first_name,
    last_name: values.last_name,
  }

  try {
    await userStore.updateProfile(payload)
    toast({ title: 'Success', description: 'Profile updated successfully!' })
    originalUsername.value = values.username
    originalEmail.value = values.email
    resetForm({ values: values })
  } catch (error: any) {
    console.error('Error updating profile:', error)
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to update profile. Please try again.'
    toast({ title: 'Error', description: errorMessage, variant: 'destructive' })
  }
})

const uploadAvatar = async (file: File) => {
  if (!file) return

  const reader = new FileReader()
  reader.onload = async (e) => {
    const base64String = e.target?.result as string
    try {
      await userStore.updateAvatar({ avatar_image: base64String })
      if (route.query.no_profile_picture) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.push('/')
      }
      await userStore.fetchCurrentUser()
      toast({ title: 'Success', description: 'Avatar updated successfully!' })
      fileInputKey.value++
    } catch (error: any) {
      console.error('Error updating avatar:', error)
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to update avatar.'
      toast({ title: 'Error', description: errorMessage, variant: 'destructive' })
      fileInputKey.value++
    }
  }
  reader.onerror = (error) => {
    console.error('Error reading file:', error)
    toast({ title: 'Error', description: 'Failed to read file.', variant: 'destructive' })
    fileInputKey.value++
  }
  reader.readAsDataURL(file)
}

const handleFileSelection = (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (file) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    uploadAvatar(file)
  }
}

const removeAvatar = async () => {
  try {
    await userStore.deleteAvatar()
    if (userStore.currentUser) {
      userStore.currentUser.avatar_image = undefined
    }
    toast({ title: 'Success', description: 'Avatar removed successfully!' })
  } catch (error: any) {
    console.error('Error removing avatar:', error)
    const errorMessage =
      error.response?.data?.message || error.message || 'Failed to remove avatar.'
    toast({ title: 'Error', description: errorMessage, variant: 'destructive' })
  }
}

// Add this function to persistently check and reapply validation errors
const setupPersistentValidation = () => {
  // Create an interval that constantly checks and reapplies errors if needed
  const validationInterval = setInterval(() => {
    if (!validationState.value.usernameValid && values.username !== originalUsername.value) {
      setFieldError('username', 'This username is already taken')
    }
    
    if (!validationState.value.emailValid && values.email !== originalEmail.value) {
      setFieldError('email', 'This email is already registered')
    }
  }, 200) // Check every 200ms
  
  // Clean up when component unmounts
  onUnmounted(() => {
    clearInterval(validationInterval)
  })
}

// Reset validation state when values match original
watch(() => values.username, (newValue) => {
  if (newValue === originalUsername.value) {
    validationState.value.usernameValid = true
  }
})

watch(() => values.email, (newValue) => {
  if (newValue === originalEmail.value) {
    validationState.value.emailValid = true
  }
})

// Call the setup function to enable constant validation
setupPersistentValidation()

const handleBack = () => {
  if (window.history.length > 2) {
    router.back()
  } else {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push('/dashboard')
  }
}
</script>

<style scoped></style>
